// FilePreviewModal.tsx - Complete File
import React from 'react';
import { Modal } from 'reactstrap';
import { 
  FileText, FileImage, FileVideo, File, FileType, 
  Table, MonitorPlay, FileCode, X, Download 
} from "lucide-react";

interface FilePreviewProps {
  file: any;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (file: any) => void;
}

// File type to icon mapping - copy from your existing code
const getFileIcon = (filename: string, size: number = 24) => {
  const ext = filename?.split(".")?.pop()?.toLowerCase() || "";

  switch (ext) {
    // Documents
    case "doc":
    case "docx":
    case "txt":
      return <FileText size={size} />;

    // Images
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return <FileImage size={size} />;

    // Videos
    case "mp4":
    case "mov":
    case "avi":
    case "mkv":
    case "webm":
      return <FileVideo size={size} />;

    // PDF
    case "pdf":
      return <FileType size={size} />;

    // Spreadsheets
    case "csv":
    case "xlsx":
    case "xls":
      return <Table size={size} />;

    // Presentations
    case "ppt":
    case "pptx":
      return <MonitorPlay size={size} />;

    // Code files
    case "js":
    case "ts":
    case "html":
    case "css":
    case "jsx":
    case "tsx":
      return <FileCode size={size} />;

    // Default
    default:
      return <File size={size} />;
  }
};

const FilePreviewModal: React.FC<FilePreviewProps> = ({ file, isOpen, onClose, onDownload }) => {
  if (!file) return null;
  
  const fileUrl = file.bucketUrl + file.filePath;
  const fileExtension = file.fileName.split('.').pop()?.toLowerCase() || '';
  
  // Helper to determine file type
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
  const isPdf = fileExtension === 'pdf';
  const isVideo = ['mp4', 'webm', 'ogv'].includes(fileExtension);
  
  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <div className="modal-header">
        <h5 className="modal-title">{file.fileName}</h5>
        <button type="button" className="close" onClick={onClose}>
          <X size={24} />
        </button>
      </div>
      <div className="modal-body">
        <div className="file-preview-container">
          {isImage && (
            <div className="image-preview text-center">
              <img 
                src={fileUrl} 
                alt={file.fileName} 
                className="img-fluid" 
                style={{ maxHeight: '70vh' }}
              />
            </div>
          )}
          
          {isPdf && (
            <div className="pdf-preview">
              <iframe 
                src={fileUrl} 
                width="100%" 
                height="500px" 
                title={file.fileName}
                className="border-0"
              />
            </div>
          )}
          
          {isVideo && (
            <div className="video-preview text-center">
              <video 
                controls 
                src={fileUrl} 
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}
          
          {!isImage && !isPdf && !isVideo && (
            <div className="unsupported-preview text-center py-5">
              <div className="mb-3">
                {getFileIcon(file.fileName, 64)}
              </div>
              <h5>Preview not available for this file type</h5>
              <p className="text-muted">Please download the file to view its contents.</p>
              <button 
                className="btn btn-primary"
                onClick={() => onDownload(file)}
              >
                Download {file.fileName}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FilePreviewModal;